import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import * as api from "./csaApi";

/**
 * One audit, as a dashboard (design §10).
 *
 * The trend answers "is this site getting better". This answers "what did we
 * find on this visit" — the question anyone opening a single report actually
 * has, and the one the trend cannot answer at all.
 *
 * `HSEDashboard` charts severity, keyed on criticality. CSA has no
 * criticality, so the analogue is the **answer-state distribution**: how many
 * questions came back Yes, Partial, No and N/A, per pillar.
 *
 * The distinction this screen exists to keep visible is between a **No** and a
 * **scoped-out** question. Both leave a pillar short of 163 answers, and they
 * mean opposite things: one is a finding, the other is a statement that nobody
 * assessed it and the client agreed. Merging them into "not compliant" is the
 * single most damaging thing this screen could do, so scoped-out is a separate
 * bar in a separate colour, and the denominator is stated beside it.
 */

const STATES = [
  { key: "yes", label: "Yes", colour: "#2e7d4f" },
  { key: "partial", label: "Partial", colour: "#b8860b" },
  { key: "no", label: "No", colour: "#c0392b" },
  { key: "na", label: "N/A", colour: "#7c8a87" },
  { key: "scoped_out", label: "Scoped out", colour: "#b9c4c1" },
  { key: "unanswered", label: "Not answered", colour: "#e2e8e6" },
];

export default function CsaAuditInsight({ auditId, onBack }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openPillar, setOpenPillar] = useState(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    api
      .auditInsight(auditId)
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [auditId]);

  /* A stacked bar per pillar. Stacked rather than grouped because the bar
     length is then the pillar's question count, which makes a pillar with 61
     questions look bigger than one with 12 — which it is. */
  const chart = useMemo(() => {
    if (!data) return null;
    const names = data.pillars.map((p) => "P" + p.no);
    return {
      series: STATES.map((s) => ({
        name: s.label,
        data: data.pillars.map((p) => p.counts[s.key] || 0),
      })),
      options: {
        chart: { type: "bar", stacked: true, toolbar: { show: false }, fontFamily: "inherit" },
        colors: STATES.map((s) => s.colour),
        plotOptions: { bar: { horizontal: true, barHeight: "62%" } },
        dataLabels: { enabled: false },
        xaxis: { categories: names, title: { text: "questions" } },
        legend: { position: "bottom", fontSize: "12px" },
        tooltip: {
          y: { formatter: (v) => v + (v === 1 ? " question" : " questions") },
        },
        grid: { borderColor: "#dce3e1" },
      },
    };
  }, [data]);

  if (error) return <p className="cd-error">{error}</p>;
  if (!data) return <p className="cd-empty">Loading the audit…</p>;

  const a = data.audit;
  const band = (data.bands || []).find((b) => b.name === a.maturity_band);
  const t = data.totals;

  return (
    <div className="cd-single">
      {/* ---------------- headline ---------------- */}
      <div className="cd-card cd-headline">
        <div className="cd-hl-left">
          <div className="cd-hl-pct">{a.overall_pct == null ? "—" : a.overall_pct + "%"}</div>
          <span
            className="cd-band"
            style={{ background: (band && band.color) || "#7c8a87" }}
          >
            {a.maturity_band || "Not scored"}
          </span>
          {band ? <span className="cd-hl-range">{band.min_pct}–{band.max_pct}%</span> : null}
        </div>

        <div className="cd-hl-right">
          {/* The thing the reader was asking about: what the word means. */}
          {a.band_statement ? <p className="cd-hl-says">{a.band_statement}</p> : null}
          <p className="cd-hl-meta">
            {a.report_id} · visited {fmtDate(a.start_date)}
            {a.submitted_at ? ` · submitted ${fmtDate(a.submitted_at)}` : ""}
            {a.status !== "submitted" && a.status !== "reviewed" ? ` · ${a.status}` : ""}
          </p>
        </div>
      </div>

      {/* ---------------- the counts ----------------

          Stated before any chart. "143 Yes, 1 Partial, 4 No" is the finding;
          the chart only shows where they sit. */}
      <div className="cd-tiles">
        {STATES.map((s) =>
          s.key === "unanswered" && !t.unanswered ? null : (
            <div className="cd-tile" key={s.key}>
              <span className="cd-dot" style={{ background: s.colour }} />
              <span className="cd-tile-n">{t[s.key] || 0}</span>
              <span className="cd-tile-l">{s.label}</span>
            </div>
          )
        )}
      </div>

      <p className="cd-denominator">
        <strong>{t.assessed}</strong> of {t.questions} questions were assessed.
        {t.scoped_out ? (
          <>
            {" "}
            {t.scoped_out} were scoped out and are not counted in the score —
            they are not findings.
          </>
        ) : null}
        {t.unanswered ? (
          <> {t.unanswered} are still unanswered.</>
        ) : null}
      </p>

      {/* ---------------- per pillar ---------------- */}
      <section className="cd-card">
        <h3>Answers by pillar</h3>
        {chart ? (
          <Chart options={chart.options} series={chart.series} type="bar" height={300} />
        ) : null}
      </section>

      <section className="cd-card">
        <h3>Pillar detail</h3>
        <table className="cd-pillars">
          <thead>
            <tr>
              <th>Pillar</th>
              <th className="n">Score</th>
              <th className="n">Yes</th>
              <th className="n">Partial</th>
              <th className="n">No</th>
              <th className="n">Scoped out</th>
              <th className="n">Assessed</th>
            </tr>
          </thead>
          <tbody>
            {data.pillars.map((p) => (
              <React.Fragment key={p.no}>
                <tr
                  className={"cd-prow" + (openPillar === p.no ? " open" : "")}
                  onClick={() => setOpenPillar(openPillar === p.no ? null : p.no)}
                >
                  <td>
                    <span className="cd-caret">{openPillar === p.no ? "▾" : "▸"}</span>
                    <span className="cd-pno">P{p.no}</span> {p.name}
                  </td>
                  <td className="n">{p.pct == null ? "—" : p.pct + "%"}</td>
                  <td className="n">{p.counts.yes}</td>
                  <td className="n">{p.counts.partial || "—"}</td>
                  <td className={"n" + (p.counts.no ? " bad" : "")}>{p.counts.no || "—"}</td>
                  <td className="n muted">{p.counts.scoped_out || "—"}</td>
                  <td className="n muted">
                    {p.assessed} of {p.total}
                  </td>
                </tr>

                {openPillar === p.no
                  ? p.sub_pillars.map((s) => (
                      <tr className="cd-srow" key={s.name}>
                        <td className="cd-sname">{s.name}</td>
                        <td className="n">—</td>
                        <td className="n">{s.counts.yes}</td>
                        <td className="n">{s.counts.partial || "—"}</td>
                        <td className={"n" + (s.counts.no ? " bad" : "")}>{s.counts.no || "—"}</td>
                        <td className="n muted">{s.counts.scoped_out || "—"}</td>
                        <td className="n muted">
                          {s.assessed} of {s.total}
                        </td>
                      </tr>
                    ))
                  : null}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        <p className="cd-foot">A row opens to show its sub-pillars.</p>
      </section>

      {/* ---------------- what was scoped out ----------------

          With the justification, because that is what prints in the client's
          report against every question it covers. */}
      {data.exclusions && data.exclusions.length ? (
        <section className="cd-card">
          <h3>Scoped out of this audit</h3>
          {data.exclusions.map((e) => (
            <div className="cd-excl" key={e.level + e.level_value}>
              <div className="cd-excl-what">
                <span className="cd-level">{e.level.replace("_", "-")}</span>
                <strong>{e.level_value}</strong>
              </div>
              <p>{e.justification}</p>
            </div>
          ))}
        </section>
      ) : null}

      {/* ---------------- the findings ---------------- */}
      <section className="cd-card">
        <h3>
          Findings <span className="cd-count">{data.gaps.length}</span>
        </h3>
        {data.gaps.length ? (
          <table className="cd-gaps">
            <thead>
              <tr>
                <th>Question</th>
                <th>Answer</th>
                <th>What was seen</th>
              </tr>
            </thead>
            <tbody>
              {data.gaps.map((g) => (
                <tr key={g.question_code}>
                  <td>
                    <span className="mono">{g.question_code}</span>
                    <span className="cd-where">
                      P{g.pillar_no} · {g.sub_pillar}
                    </span>
                  </td>
                  <td>
                    <span className={"cd-ans a-" + g.answer}>
                      {g.answer === "no" ? "No" : "Partial"}
                    </span>
                  </td>
                  <td>{g.comment || <em className="muted">no note recorded</em>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="cd-foot">Nothing was marked No or Partial in this audit.</p>
        )}
      </section>

      {onBack ? (
        <button type="button" className="cd-back" onClick={onBack}>
          ‹ Back to the trend
        </button>
      ) : null}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split("-");
  if (!y || !m || !day) return s;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${Number(day)} ${months[Number(m) - 1] || m} ${y}`;
}
